export type ParentFieldLayoutItem = {
  key: string;
  /** Unidades de la grilla que ocupa el campo (default 2 = una “celda” del layout legacy de 4 columnas). */
  colSpan: number;
};

export type ParentFieldLayoutConfig = {
  /** Unidades por fila. Default 8 (= 4 celdas legacy × 2). */
  cellsPerRow: number;
  fields: ParentFieldLayoutItem[];
};

export type ParentFieldQuestion = {
  key: string;
  label: string;
  answer?: string;
  item?: any;
};

export const remapPlastyvergParentFieldLabel = (label?: string | null) => {
  const value = String(label || '');
  if (/^soporte\s+conos$/i.test(value.trim())) return 'SOPORTACONOS';
  return value;
};

/** Antes: 4 columnas (`md={3}` / PDF ×4). Ahora: 8 unidades con colSpan default 2. */
export const DEFAULT_PARENT_FIELD_CELLS_PER_ROW = 8;
export const DEFAULT_PARENT_FIELD_COL_SPAN = 2;
export const PARENT_FIELD_CELLS_PER_ROW_OPTIONS = [4, 6, 8, 12] as const;

/** @deprecated alias: columnas PDF = cellsPerRow */
export const PARENT_FIELD_LAYOUT_COLUMNS = DEFAULT_PARENT_FIELD_CELLS_PER_ROW;

const clampInt = (value: unknown, min: number, max: number, fallback: number) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
};

export const normalizeCellsPerRow = (raw: unknown): number => {
  const value = clampInt(raw, 1, 24, DEFAULT_PARENT_FIELD_CELLS_PER_ROW);
  return value;
};

/** Migra span legacy half/full o colSpan numérico. */
export const normalizeFieldColSpan = (raw: unknown, cellsPerRow = DEFAULT_PARENT_FIELD_CELLS_PER_ROW): number => {
  if (raw === 'half' || raw === 1 || raw === '1') return 1;
  if (raw === 'full') {
    // Interpretación previa (fila completa) era demasiado amplia; en el modelo de celda
    // "full" = una celda legacy = DEFAULT_PARENT_FIELD_COL_SPAN.
    return Math.min(DEFAULT_PARENT_FIELD_COL_SPAN, cellsPerRow);
  }
  return clampInt(raw, 1, cellsPerRow, DEFAULT_PARENT_FIELD_COL_SPAN);
};

export const normalizeParentFieldLayoutItems = (
  raw: unknown,
  cellsPerRow = DEFAULT_PARENT_FIELD_CELLS_PER_ROW,
): ParentFieldLayoutItem[] => {
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const result: ParentFieldLayoutItem[] = [];

  for (const entry of raw) {
    if (typeof entry === 'string') {
      const key = entry.trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      result.push({key, colSpan: Math.min(DEFAULT_PARENT_FIELD_COL_SPAN, cellsPerRow)});
      continue;
    }

    if (entry && typeof entry === 'object' && typeof (entry as any).key === 'string') {
      const key = String((entry as any).key).trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const colSpanRaw = (entry as any).colSpan ?? (entry as any).span;
      result.push({
        key,
        colSpan: normalizeFieldColSpan(colSpanRaw, cellsPerRow),
      });
    }
  }

  return result;
};

/**
 * Normaliza:
 * - `string[]` / `{key,span|colSpan}[]` (legacy)
 * - `{ cellsPerRow, fields }`
 */
export const normalizeParentFieldLayoutConfig = (raw: unknown): ParentFieldLayoutConfig => {
  if (Array.isArray(raw)) {
    const cellsPerRow = DEFAULT_PARENT_FIELD_CELLS_PER_ROW;
    return {
      cellsPerRow,
      fields: normalizeParentFieldLayoutItems(raw, cellsPerRow),
    };
  }

  if (raw && typeof raw === 'object') {
    const cellsPerRow = normalizeCellsPerRow((raw as any).cellsPerRow);
    const fieldsRaw = (raw as any).fields ?? (raw as any).parentQuestionKeys ?? (raw as any).items;
    return {
      cellsPerRow,
      fields: normalizeParentFieldLayoutItems(fieldsRaw, cellsPerRow),
    };
  }

  return {cellsPerRow: DEFAULT_PARENT_FIELD_CELLS_PER_ROW, fields: []};
};

/** Compat: solo el array de fields (legacy callers). */
export const normalizeParentFieldLayout = (raw: unknown): ParentFieldLayoutItem[] =>
  normalizeParentFieldLayoutConfig(raw).fields;

export const filterParentFieldLayoutByKeys = (
  layout: ParentFieldLayoutItem[],
  availableKeys: Iterable<string>,
): ParentFieldLayoutItem[] => {
  const keys = availableKeys instanceof Set ? availableKeys : new Set(availableKeys);
  return layout.filter((item) => keys.has(item.key));
};

export const filterParentFieldLayoutConfigByKeys = (
  config: ParentFieldLayoutConfig,
  availableKeys: Iterable<string>,
): ParentFieldLayoutConfig => ({
  cellsPerRow: config.cellsPerRow,
  fields: filterParentFieldLayoutByKeys(config.fields, availableKeys),
});

export type PackedParentFieldSlot<T extends {colSpan: number} = ParentFieldLayoutItem> =
  | {kind: 'field'; item: T}
  | {kind: 'empty'; colSpan: number};

export type PackedParentFieldRow<T extends {colSpan: number} = ParentFieldLayoutItem> =
  PackedParentFieldSlot<T>[];

/** Empaqueta campos en filas de `cellsPerRow` unidades. */
export const packParentFieldsIntoRows = <T extends {colSpan: number}>(
  items: T[],
  cellsPerRow: number = DEFAULT_PARENT_FIELD_CELLS_PER_ROW,
): PackedParentFieldRow<T>[] => {
  const capacity = Math.max(1, cellsPerRow);
  const rows: PackedParentFieldRow<T>[] = [];
  let current: PackedParentFieldRow<T> = [];
  let used = 0;

  const flush = () => {
    if (!current.length) return;
    if (used < capacity) {
      current.push({kind: 'empty', colSpan: capacity - used});
    }
    rows.push(current);
    current = [];
    used = 0;
  };

  for (const item of items) {
    const units = clampInt(item.colSpan, 1, capacity, DEFAULT_PARENT_FIELD_COL_SPAN);
    const normalized = {...item, colSpan: units};

    if (used + units > capacity) {
      flush();
    }

    current.push({kind: 'field', item: normalized});
    used += units;

    if (used >= capacity) {
      flush();
    }
  }

  flush();
  return rows;
};

export type ResolvedParentField<T extends {key: string} = ParentFieldQuestion> = T & {
  colSpan: number;
};

export const resolveParentFieldsForRender = <T extends {key: string}>(
  questions: T[],
  layout: ParentFieldLayoutItem[],
  options: {
    whenEmptyUseAll?: boolean;
    defaultColSpan?: number;
    cellsPerRow?: number;
  } = {},
): ResolvedParentField<T>[] => {
  const {
    whenEmptyUseAll = false,
    defaultColSpan = DEFAULT_PARENT_FIELD_COL_SPAN,
    cellsPerRow = DEFAULT_PARENT_FIELD_CELLS_PER_ROW,
  } = options;
  const byKey = new Map(questions.map((question) => [question.key, question]));
  const cappedDefault = Math.min(defaultColSpan, cellsPerRow);

  if (!layout.length) {
    if (!whenEmptyUseAll) return [];
    return questions.map((question) => ({...question, colSpan: cappedDefault}));
  }

  const resolved: ResolvedParentField<T>[] = [];
  for (const item of layout) {
    const question = byKey.get(item.key);
    if (!question) continue;
    resolved.push({
      ...question,
      colSpan: clampInt(item.colSpan, 1, cellsPerRow, cappedDefault),
    });
  }
  return resolved;
};

export const reorderParentFieldLayout = (
  layout: ParentFieldLayoutItem[],
  fromIndex: number,
  toIndex: number,
): ParentFieldLayoutItem[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return layout;
  if (fromIndex >= layout.length || toIndex >= layout.length) return layout;
  const next = [...layout];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export const toggleParentFieldInLayout = (
  layout: ParentFieldLayoutItem[],
  key: string,
  defaultColSpan: number = DEFAULT_PARENT_FIELD_COL_SPAN,
): ParentFieldLayoutItem[] => {
  if (layout.some((item) => item.key === key)) {
    return layout.filter((item) => item.key !== key);
  }
  return [...layout, {key, colSpan: defaultColSpan}];
};

export const setParentFieldColSpanInLayout = (
  layout: ParentFieldLayoutItem[],
  key: string,
  colSpan: number,
  cellsPerRow: number = DEFAULT_PARENT_FIELD_CELLS_PER_ROW,
): ParentFieldLayoutItem[] =>
  layout.map((item) =>
    item.key === key
      ? {...item, colSpan: clampInt(colSpan, 1, cellsPerRow, DEFAULT_PARENT_FIELD_COL_SPAN)}
      : item,
  );

export const setParentFieldLayoutCellsPerRow = (
  config: ParentFieldLayoutConfig,
  cellsPerRow: number,
): ParentFieldLayoutConfig => {
  const nextCells = normalizeCellsPerRow(cellsPerRow);
  return {
    cellsPerRow: nextCells,
    fields: config.fields.map((field) => ({
      ...field,
      colSpan: clampInt(field.colSpan, 1, nextCells, DEFAULT_PARENT_FIELD_COL_SPAN),
    })),
  };
};

type ParentAnswerCardBuilder = (question: {label: string; answer: string}) => any;

const defaultParentAnswerCard: ParentAnswerCardBuilder = (question) => ({
  stack: [
    {text: question.label, fontSize: 6.5, color: '#666666', margin: [0, 0, 0, 2]},
    {text: question.answer || '-', fontSize: 7},
  ],
  margin: [4, 4, 4, 4],
});

const pushColSpanCell = (cells: any[], content: any, colSpan: number) => {
  const span = Math.max(1, colSpan);
  cells.push(span > 1 ? {...content, colSpan: span} : content);
  for (let i = 1; i < span; i += 1) {
    cells.push({});
  }
};

export type ParentAnswerPdfGrid = {
  cellsPerRow: number;
  body: any[][];
};

/**
 * Construye tabla pdfmake de `cellsPerRow` columnas con colSpan por campo.
 * Layout vacío + whenEmptyUseAll → todas las preguntas (export consolidado).
 */
export const buildParentAnswerPdfGrid = (
  questions: Array<{key: string; label: string; answer?: string}>,
  layoutRaw: unknown,
  options: {
    whenEmptyUseAll?: boolean;
    blankAnswers?: boolean;
    buildCard?: ParentAnswerCardBuilder;
    cellsPerRow?: number;
  } = {},
): ParentAnswerPdfGrid => {
  const {whenEmptyUseAll = true, blankAnswers = false, buildCard = defaultParentAnswerCard} = options;
  const config = normalizeParentFieldLayoutConfig(layoutRaw);
  const cellsPerRow = options.cellsPerRow
    ? normalizeCellsPerRow(options.cellsPerRow)
    : config.cellsPerRow;

  const resolved = resolveParentFieldsForRender(questions, config.fields, {
    whenEmptyUseAll,
    cellsPerRow,
  }).map((question) => ({
    ...question,
    answer: blankAnswers ? ' ' : question.answer || '-',
  }));

  if (!resolved.length) {
    return {cellsPerRow, body: []};
  }

  const body = packParentFieldsIntoRows(resolved, cellsPerRow).map((row) => {
    const cells: any[] = [];

    for (const slot of row) {
      if (slot.kind === 'empty') {
        pushColSpanCell(cells, {text: ''}, slot.colSpan);
        continue;
      }

      const card = buildCard({
        label: slot.item.label,
        answer: slot.item.answer || (blankAnswers ? ' ' : '-'),
      });
      pushColSpanCell(cells, card, slot.item.colSpan);
    }

    while (cells.length < cellsPerRow) {
      cells.push({text: ''});
    }

    return cells.slice(0, cellsPerRow);
  });

  return {cellsPerRow, body};
};

/** @deprecated use buildParentAnswerPdfGrid */
export const buildParentAnswerPdfGridBody = (
  questions: Array<{key: string; label: string; answer?: string}>,
  layoutRaw: unknown,
  options: {
    whenEmptyUseAll?: boolean;
    blankAnswers?: boolean;
    buildCard?: ParentAnswerCardBuilder;
  } = {},
): any[][] => buildParentAnswerPdfGrid(questions, layoutRaw, options).body;

/** Alias legacy por si quedó importado como setParentFieldSpanInLayout */
export const setParentFieldSpanInLayout = (
  layout: ParentFieldLayoutItem[],
  key: string,
  span: 'half' | 'full' | number,
  cellsPerRow: number = DEFAULT_PARENT_FIELD_CELLS_PER_ROW,
): ParentFieldLayoutItem[] =>
  setParentFieldColSpanInLayout(layout, key, normalizeFieldColSpan(span, cellsPerRow), cellsPerRow);
